// The dashboard's shared furniture: the surfaces every section is drawn on,
// and the one thing this page does more than any other — label something and
// hang the rule for reading it off the label.

import type { ReactNode } from "react";

import { HoverCard, Panel, styled, Text } from "../kit";
import { wash } from "./palette";

/**
 * A card on the page.
 *
 * `solid` at every call site rather than a default prop: React 19 drops
 * `defaultProps` on function components, and a default that silently stops
 * applying is a translucent panel over a board nobody meant to see through.
 */
export const Card = styled(Panel)(({ theme }) => ({
  padding: theme.kit.space.lg,
  // A column with a gap, because every card here is a stack of unrelated
  // blocks — a header row, a chart, a paragraph — and margins between them
  // would be four decisions instead of one.
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.sm,
  minWidth: 0
}));

/** A section heading. */
export const Heading = styled("h2")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 18,
  lineHeight: 1.1,
  color: theme.kit.color.ink,
  margin: `${theme.kit.space.xl}px 0 ${theme.kit.space.sm}px`
}));

/** A column label, in the display voice. */
export const ColLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontWeight: 600,
  fontSize: 10,
  letterSpacing: ".06em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim
}));

export const Spacer = styled("div")({ flex: 1 });

export const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.kit.space.sm
}));

/** A lamp: live, connecting, or not. Colour is handed in from the caller. */
export const Lamp = styled("span")({
  width: 8,
  height: 8,
  flexShrink: 0,
  borderRadius: "50%"
});

/** A small chip in a line's own colour — an outcome badge, an island's beat. */
export const Tag = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.kit.space.xs,
  padding: "2px 8px",
  borderRadius: theme.kit.radius.sm,
  fontFamily: theme.kit.font.body,
  fontWeight: 700,
  fontSize: 11
}));

/** A chip's ground, at the fraction the page has always used. */
export const tagSkin = (colour: string) => ({ background: wash(colour, 18), color: colour });

/**
 * A label with its rule beside it.
 *
 * This page is dense with numbers whose meaning is a paragraph — what a basis
 * is, why the loss line is not a target, what "gross" means when the terms pull
 * opposite ways — and the paragraph belongs on the label rather than in the
 * flow. It was eighteen native `title`s; it is one primitive now.
 *
 * The dotted underline stays: it is what says there is something to hover.
 */
const Dotted = styled(Text)(({ theme }) => ({
  borderBottom: `1px dotted ${wash(theme.kit.color.inkDim, 70)}`,
  cursor: "help"
}));

export function Legend({
  text,
  rule,
  title,
  placement = "bottom"
}: {
  text: ReactNode;
  /** The rule for reading it. */
  rule: ReactNode;
  /** The heading on the card, when the label is not its own name. */
  title?: ReactNode;
  placement?: "top" | "bottom" | "right" | "left";
}): React.ReactElement {
  return (
    <HoverCard placement={placement} title={title} card={rule} maxWidth={340}>
      <Dotted tone="dim" size={12}>
        {text}
      </Dotted>
    </HoverCard>
  );
}
